import { useCallback, useEffect, useRef } from "preact/hooks";
import { safeLocalStorage } from "../services/utils";

type Reply = (message: string) => void;

type UseListenToResultStepOptions = {
  enabled?: boolean;
  onResultStep: (replyCallback: Reply) => void;
};

export function useListenToResultStep({
  enabled = true,
  onResultStep,
}: UseListenToResultStepOptions) {
  const onResultStepRef = useRef(onResultStep);
  onResultStepRef.current = onResultStep;

  const broadCast = useRef(new BroadcastChannel("resultStep"));

  useEffect(() => {
    if (!enabled) return;

    const bc = broadCast.current;
    bc.onmessage = (event) => {
      if (event.data === "EmbedResultShown") {
        onResultStepRef.current((message) => {
          bc.postMessage(message);
        });
      }
    };

    return () => {
      bc?.close();
    };
  }, [enabled]);

  // Fall back for the case where the event is not triggered
  useEffect(() => {
    const handleStorage = ({ key, newValue }: StorageEvent) => {
      if (key === "resultStep" && newValue === "EmbedResultShown") {
        onResultStepRef.current(() => {
          safeLocalStorage.removeItem("resultStep");
        });
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const replyToResultStep = useCallback((message: string) => {
    broadCast.current?.postMessage(message);
  }, []);

  return replyToResultStep;
}
